import { useState } from '@biletiniz/ui-core';
import { useMutation } from '@biletiniz/apollo';
import { VisaProviderType } from '@biletiniz/shared';
import {
  getDubaiVisaOptions,
  getRussiaVisaOptions,
} from '@biletiniz/web-app-utils';
import { VISA_STORE_DATA_MUTATION } from './gql';

const mapToBiletinizPassenger = passenger => ({
  givenName: passenger.firstName,
  surname: passenger.lastName,
  gender: passenger.gender,
  visaType: passenger.visaType.value,
  email: passenger.email,
  telephone: passenger.gsmNumber,
  birthDate: `${passenger.dateOfBirth.year}-${passenger.dateOfBirth.month}-${passenger.dateOfBirth.day}`,
  departureDate: passenger.departureDate,
});

const passengerInitialValues = (
  firstName = '',
  lastName = '',
  gender = 'male',
  visaStartDate = undefined,
  dateOfBirth = {
    day: '',
    month: '',
    year: '',
  },
  visaType,
  email = '',
  gsmNumber = '',
  departureDate = undefined
) => {
  let day = '';
  let month = '';
  let year = '';
  if (typeof dateOfBirth === 'string') {
    [year, month, day] = dateOfBirth.split('-');
  } else if (typeof dateOfBirth === 'object' && dateOfBirth !== null) {
    ({ day, month, year } = dateOfBirth);
  }

  return {
    firstName,
    lastName,
    gender,
    visaStartDate,
    dateOfBirth: {
      day,
      month,
      year,
    },
    visaType,
    email,
    gsmNumber,
    departureDate,
  };
};

const emptyInitialValues = (
  visaProviderType,
  dubaiVisaOptions,
  russiaVisaOptions
) => ({
  travellers: [
    passengerInitialValues(
      null,
      null,
      'male',
      null,
      null,
      visaProviderType === VisaProviderType.DUBAI
        ? dubaiVisaOptions[0]
        : russiaVisaOptions[0]
    ),
  ],
  cardHolderName: '',
  cardNumber: '',
  expiryDate: '',
  cvc: '',
  termsOfService: false,
  adultAtLeast: undefined,
});

const sessionFullInitialValues = (dubaiVisaBookingInfo, dubaiVisaOptions) => ({
  travellers: dubaiVisaBookingInfo?.passengers?.reduce(
    (result, passengerDetails) => {
      if (passengerDetails) {
        result.push(
          passengerInitialValues(
            passengerDetails.givenName,
            passengerDetails.surname,
            passengerDetails.gender,
            passengerDetails.visaStartDate,
            passengerDetails.birthDate,
            dubaiVisaOptions.find(
              element => element.value === passengerDetails.visaType
            ),
            passengerDetails.email,
            `0 (${passengerDetails.telephone.substr(
              0,
              3
            )}) ${passengerDetails?.telephone.substr(
              3,
              3
            )} ${passengerDetails?.telephone.substr(
              6,
              2
            )} ${passengerDetails?.telephone.substr(8)}`,
            passengerDetails.departureDate
          )
        );
      }
      return result;
    },
    []
  ),
  cardHolderName: dubaiVisaBookingInfo?.paymentCard.cardHolderName,
  cardNumber: `${dubaiVisaBookingInfo?.paymentCard.cardNumber.substr(
    0,
    4
  )}-${dubaiVisaBookingInfo?.paymentCard.cardNumber.substr(
    4,
    4
  )}-${dubaiVisaBookingInfo?.paymentCard.cardNumber.substr(
    8,
    4
  )}-${dubaiVisaBookingInfo?.paymentCard.cardNumber.substr(12)}`,
  expiryDate: `${dubaiVisaBookingInfo?.paymentCard.expireMonth}/${dubaiVisaBookingInfo?.paymentCard.expireYear}`,
  cvc: dubaiVisaBookingInfo?.paymentCard.cvc,
  termsOfService: false,
});

const RussiaSessionFullInitialValues = (
  russiaVisaBookingInfo,
  russiaVisaOptions
) => ({
  travellers: russiaVisaBookingInfo?.passengers?.reduce(
    (result, passengerDetails) => {
      if (passengerDetails) {
        result.push(
          passengerInitialValues(
            passengerDetails.givenName,
            passengerDetails.surname,
            passengerDetails.gender,
            passengerDetails.visaStartDate,
            passengerDetails.birthDate,
            russiaVisaOptions.find(
              element => element.value === passengerDetails.visaType
            ),
            passengerDetails.email,
            `0 (${passengerDetails.telephone.substr(
              0,
              3
            )}) ${passengerDetails?.telephone.substr(
              3,
              3
            )} ${passengerDetails?.telephone.substr(
              6,
              2
            )} ${passengerDetails?.telephone.substr(8)}`,
            passengerDetails.departureDate
          )
        );
      }
      return result;
    },
    []
  ),
  cardHolderName: russiaVisaBookingInfo?.paymentCard.cardHolderName,
  cardNumber: `${russiaVisaBookingInfo?.paymentCard.cardNumber.substr(
    0,
    4
  )}-${russiaVisaBookingInfo?.paymentCard.cardNumber.substr(
    4,
    4
  )}-${russiaVisaBookingInfo?.paymentCard.cardNumber.substr(
    8,
    4
  )}-${russiaVisaBookingInfo?.paymentCard.cardNumber.substr(12)}`,
  expiryDate: `${russiaVisaBookingInfo?.paymentCard.expireMonth}/${russiaVisaBookingInfo?.paymentCard.expireYear}`,
  cvc: russiaVisaBookingInfo?.paymentCard.cvc,
  termsOfService: false,
});

export const useVisa = (
  i18n,
  visaProviderType,
  dubaiVisaBookingInfo,
  russiaVisaBookingInfo
) => {
  const [iframeUrl, setIframeUrl] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const [
    visaStoreDataMutation,
    { loading: storeLoading, error: storeError },
  ] = useMutation(VISA_STORE_DATA_MUTATION);

  const applyVisa = async (travellerInfo, visaType) => {
    const formData = new FormData();
    travellerInfo.travellers.map(traveller =>
      formData.append('personalPhoto', traveller.personalPhoto)
    );
    travellerInfo.travellers.map(traveller =>
      formData.append('passportPhoto', traveller.passportPhoto)
    );

    try {
      setUploadingFiles(true);
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_ROOT_URL}/file-upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        setUploadingFiles(false);
        throw new Error(i18n.t('errors.errorContactSupport'));
      }

      const jsonResponse = await response.json();

      if (jsonResponse.success) {
        // Files saved successfully and start saving form fields
        visaStoreDataMutation({
          variables: {
            session: jsonResponse.session,
            visaType,
            passengers: travellerInfo.travellers.map(traveller =>
              mapToBiletinizPassenger(traveller)
            ),
            paymentCard: {
              cardNumber: travellerInfo.cardNumber,
              expireYear: travellerInfo.expiryDate.split('/')[1],
              expireMonth: travellerInfo.expiryDate.split('/')[0],
              cvc: travellerInfo.cvc,
              cardHolderName: travellerInfo.cardHolderName,
            },
          },
        }).then(({ data }) => {
          const apiRootUrl = process.env.NEXT_PUBLIC_API_ROOT_URL || '';
          setIframeUrl(
            `${apiRootUrl}/visa-payment-checkout/${data.visaStoreData}?type=${visaType}`
          );
          setUploadingFiles(false);
        });
      } else {
        setUploadingFiles(false);
        throw new Error(i18n.t('errors.errorContactSupport'));
      }
    } catch (error) {
      setUploadingFiles(false);
      throw new Error(i18n.t('errors.errorContactSupport'));
    }
  };

  const dubaiVisaOptions = getDubaiVisaOptions(i18n);
  const russiaVisaOptions = getRussiaVisaOptions(i18n);

  let formInitialValues = null;
  if (dubaiVisaBookingInfo) {
    formInitialValues = sessionFullInitialValues(
      dubaiVisaBookingInfo,
      dubaiVisaOptions
    );
  } else if (russiaVisaBookingInfo) {
    formInitialValues = RussiaSessionFullInitialValues(
      russiaVisaBookingInfo,
      russiaVisaOptions
    );
  } else {
    formInitialValues = emptyInitialValues(
      visaProviderType,
      dubaiVisaOptions,
      russiaVisaOptions
    );
  }

  return {
    applyVisa,
    iframeUrl,
    setIframeUrl,
    formInitialValues,
    storeLoading: storeLoading || uploadingFiles,
    storeError: storeError?.message,
  };
};
